import React, { FC, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { CaseStudyPageTransitionDuration } from 'constants/values';
import {
  CaseStudyPage,
  CaseStudies,
  LandingMenuSnippetPages,
  Settings,
} from 'lib/sanity/types';

import cx from 'classnames';
import BlockSwitch from 'components/BlockSwitch';
import MoreCaseStudy from 'blocks/MoreCaseStudy';
import CaseStudySkeleton from 'blocks/CaseStudySkeleton';
import LandingTabMenuSkeleton from 'blocks/LandingTabMenuSkeleton';
import SimpleFooter from './SimpleFooter';
import RichText from 'atoms/RichText';

// TODO: remove Old Hero when each case study has been updated with new copy in Sanity

export type CaseStudyPageProps = {
  caseStudyPage: CaseStudyPage;
  settings: Settings;
  landingMenuSnippetPages: LandingMenuSnippetPages;
};

const CaseStudy: FC<CaseStudyPageProps> = ({
  settings,
  caseStudyPage,
  landingMenuSnippetPages,
}) => {
  const router = useRouter();
  const {
    title,
    headline,
    projectTags,
    byline,
    subtitle,
    contents,
    introTitledTextList,
    caseStudies,
  } = caseStudyPage;
  const [loaded, setLoaded] = useState<boolean>(true);
  const [isClickedBackToWorkBtn, setIsClickedBackToWorkBtn] =
    useState<boolean>(false);
  const [transitionStatus, setTransitionStatus] = useState<
    null | 'ready' | 'ongoing' | 'fired' | 'done'
  >(null);

  const onBackToWorkBtnClicked = () => {
    setLoaded(false);
    setIsClickedBackToWorkBtn(true);
  };

  const classForTopNavContainer =
    'font-book top-0 flex flex-row w-full p-4 desktop:px-6 desktop:py-4 items-center z-nav font-lighter-normal';

  useEffect(() => {
    const handleRouteChangeStart = (url: any) => {
      if (url === '/work') {
        setTransitionStatus('fired');
      }
    };
    const handleRouteChangeComplete = (url: any) => {
      if (url === '/work') {
        setTransitionStatus('done');
      }
    };
    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router]);

  useEffect(() => {
    if (transitionStatus === 'done') {
      const timer = setTimeout(() => {
        setLoaded(true);
        setTransitionStatus(null);
      }, CaseStudyPageTransitionDuration);
      return () => {
        clearTimeout(timer);
      };
    }
    // Move Case Study page skeleton from off-screen(top: 100%) to on-screen(top: 0%) unless it's navigating back to Landing Tab page for 'work'.
    if (transitionStatus === 'fired' && !isClickedBackToWorkBtn) {
      setTransitionStatus('ongoing');
    }
  }, [transitionStatus]);

  return (
    <>
      <div
        className={cx(`fixed top-0 left-0 w-screen h-screen`, {
          'opacity-0 pointer-events-none': loaded,
        })}
      >
        <LandingTabMenuSkeleton
          landingMenuSnippetPages={landingMenuSnippetPages}
          isFromCaseStudy={true}
        />
      </div>
      <div
        className={cx(
          `relative h-auto w-full transition-transform z-10 mb-[400px]`,
          {
            'translate-y-full': isClickedBackToWorkBtn,
          },
        )}
      >
        <div className="bg-white-tint text-black inset-0 min-h-screen rounded-b-3xl">
          <CaseStudySkeleton
            title={title}
            subtitle={subtitle}
            className={cx(`duration-${CaseStudyPageTransitionDuration}`, {
              'opacity-0 pointer-events-none': loaded,
              'translate-y-full': transitionStatus === 'ready',
              'opacity-100 translate-y-0': transitionStatus === 'ongoing',
            })}
          />
          <div className="content-max-width font-book text-xl desktop:text-desktop-xl flex flex-col space-y-14 desktop:space-y-44 pt-36 desktop:pt-44 pb-10">
            <div className="CaseStudy__inner-wrapper CaseStudy__intro">
              <div className="flex flex-col mb-10 desktop:mb-10">
                {/* Old hero, to be removed */}
                {!projectTags && (
                  <>
                    <div className="flex flex-col space-y-1 mb-10 desktop:mb-10">
                      <div>{title}</div>
                      <div className="text-black-tint">{subtitle}</div>
                    </div>
                    <div className="flex flex-col space-y-8 bg-sand rounded-sm p-4 desktop:p-8">
                      {introTitledTextList?.map((content) => {
                        const { title, description, _key } = content;
                        return (
                          <div
                            className="text-small desktop:text-desktop-small text-black flex flex-col space-y-1"
                            key={_key}
                          >
                            <h4>{title}</h4>
                            <p className="text-black-tint">{description}</p>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* New hero */}
                {projectTags && (
                  <>
                    {headline && <RichText value={headline} />}
                    <div className="mt-10">
                      <div className="inline-block space-x-1 mr-5">
                        {projectTags &&
                          projectTags.map((tag, index) => (
                            <div
                              key={index}
                              className="inline-flex content-center flex-wrap largeButton text-desktop-small text-dark-gray bg-white p-4"
                            >
                              {tag}
                            </div>
                          ))}
                      </div>
                      <div className="inline-block">
                        {byline && <RichText value={byline} />}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-col space-y-14 desktop:space-y-44">
              <BlockSwitch contents={contents} isCaseStudy={true} />
            </div>
          </div>
          <div
            className={cx(
              'font-book px-content-skinny desktop:px-content-narrow pb-content-skinny tablet:pb-content-narrow',
              {
                'pt-36 desktop:pt-72': caseStudies,
              },
            )}
          >
            {caseStudies && (
              <MoreCaseStudy
                block={caseStudies as CaseStudies}
                className="mb-28"
                title={settings.caseStudySettings.moreCaseStudiesGridTitle}
                aspectRatio={settings.caseStudySettings.gridItemAspectRatio}
              />
            )}
          </div>
        </div>
      </div>
      <footer className="fixed bottom-0 left-0 right-0 z-0 w-full h-screen bg-white pb-10 px-6 flex items-end">
        <SimpleFooter settings={settings} />
      </footer>
    </>
  );
};

export default CaseStudy;
